import React, { useState, useEffect } from 'react';
import DataService from '../../services/data.service';
import LoaderOverlay from '../common/loader.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const EditBookingQuestion = () => {
  const [question, setQuestion] = useState('');
  const [inputType, setInputType] = useState('text');
  const [isRequired, setIsRequired] = useState(false);
  const [options, setOptions] = useState(['']);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const questionData = location.state?.data;

  useEffect(() => {
    if (questionData) {
      setQuestion(questionData.question || '');
      setInputType(questionData.input_type || 'text');
      setIsRequired(questionData.is_required || false);
      setDisplayOrder(questionData.display_order || 0);
      if (questionData.options && Array.isArray(questionData.options)) {
        setOptions(questionData.options);
      } else {
        setOptions(['']);
      }
    }
  }, [questionData]);

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleRemoveOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleEditQuestion = async (e) => {
    e.preventDefault();

    if (!question.trim()) {
      setError('Question text is required.');
      return;
    }

    if (['radio', 'checkbox', 'select'].includes(inputType)) {
      const validOptions = options.filter(opt => opt.trim() !== '');
      if (validOptions.length === 0) {
        setError('At least one option is required for this input type.');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        id: questionData.id,
        question,
        input_type: inputType,
        is_required: isRequired ? 1 : 0,
        display_order: parseInt(displayOrder) || 0,
      };

      if (['radio', 'checkbox', 'select'].includes(inputType)) {
        payload.options = options.filter(opt => opt.trim() !== '');
      }

      await DataService.editBookingQuestion(payload);
      toast.success('Booking Question updated Successfully.');
      navigate('/booking-questions');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update question.');
    } finally {
      setLoading(false);
    }
  };

  if (!questionData) {
    return <div className="container mt-4">No question selected for editing.</div>;
  }

  const showOptions = ['radio', 'checkbox', 'select'].includes(inputType);
  const goBack = () => navigate(-1);

  return (
    <>
      <LoaderOverlay loading={loading} />

      <h4>Edit Booking Question</h4>

      <form onSubmit={handleEditQuestion} className="mt-3">
        <div className="mb-3">
          <label className="form-label">Question</label>
          <input
            type="text"
            className="form-control px-3"
            placeholder="Enter question text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Input Type</label>
          <select
            className="form-control px-3"
            value={inputType}
            onChange={(e) => setInputType(e.target.value)}
          >
            <option value="text">Text</option>
            <option value="textarea">Textarea</option>
            <option value="radio">Radio</option>
            <option value="checkbox">Checkbox</option>
            <option value="select">Select Dropdown</option>
          </select>
        </div>

        {showOptions && (
          <div className="mb-3">
            <label className="form-label">Options</label>
            {options.map((option, index) => (
              <div key={index} className="d-flex gap-2 mb-2">
                <input
                  type="text"
                  className="form-control px-3"
                  placeholder={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                />
                {options.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleRemoveOption(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleAddOption}
            >
              + Add Option
            </button>
          </div>
        )}

        <div className="mb-3 form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="isRequired"
            checked={isRequired}
            onChange={(e) => setIsRequired(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="isRequired">
            Required Field
          </label>
        </div>

        <div className="mb-3">
          <label className="form-label">Display Order</label>
          <input
            type="number"
            className="form-control px-3"
            placeholder="Enter display order (0 for default)"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
          />
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <button type="submit" className="btn btn-primary">
          Update Question
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          style={{ marginLeft: '15px' }}
          onClick={goBack}
        >
          ← Back
        </button>
      </form>
    </>
  );
};

export default EditBookingQuestion;